import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, Trash2, Printer, Save, X, DollarSign, User, FileText, Check, Gift } from 'lucide-react';
import { API_URL } from '../config/api';
import './SimpleReceipt.css';

const PAYMENT_METHODS = [
    { value: 'efectivo', label: 'Efectivo' },
    { value: 'tarjeta_debito', label: 'Tarjeta de Debito' },
    { value: 'tarjeta_credito', label: 'Tarjeta de Credito' },
    { value: 'transferencia', label: 'Transferencia Bancaria' },
];

const SimpleReceipt = ({ prefilledPatientStudies, onClear }) => {
    // State
    const [folio, setFolio] = useState('');
    const [date, setDate] = useState(() => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    });
    const [patientSearch, setPatientSearch] = useState('');
    const [patients, setPatients] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [showPatientList, setShowPatientList] = useState(false);

    // Service Search State
    const [serviceSearch, setServiceSearch] = useState('');
    const [showServiceList, setShowServiceList] = useState(false);

    // Fiscal Data State
    const [fiscalData, setFiscalData] = useState({
        rfc: '',
        razonSocial: '',
        usoCfdi: 'G03',
        cp: '',
        regime: ''
    });

    const [catalogStudies, setCatalogStudies] = useState([]);
    const [services, setServices] = useState([]);
    const [discount, setDiscount] = useState(0);
    const [paymentMethod, setPaymentMethod] = useState('efectivo');
    const [requiresInvoice, setRequiresInvoice] = useState(false);
    const [notes, setNotes] = useState('');
    const [doctors, setDoctors] = useState([]);
    const [selectedDoctorId, setSelectedDoctorId] = useState('');
    const saveLockRef = useRef(false);

    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [isExemptConsultation, setIsExemptConsultation] = useState(false);

    useEffect(() => {
        fetchCatalogStudies();
        generateFolio();
        fetchDoctors();
    }, []);

    const fetchDoctors = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/staff-doctors`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setDoctors([{ id: 'NA', name: 'SIN MEDICO' }, ...data]);
            }
        } catch (err) {
            console.error('Error fetching doctors:', err);
        }
    };

    // Auto-fill fiscal data
    useEffect(() => {
        if (selectedPatient) {
            console.log('Selected patient fiscal data:', {
                fiscal_rfc: selectedPatient.fiscal_rfc,
                fiscal_razon_social: selectedPatient.fiscal_razon_social,
                fiscal_uso_cfdi: selectedPatient.fiscal_uso_cfdi,
                fiscal_zip: selectedPatient.fiscal_zip,
                fiscal_regime: selectedPatient.fiscal_regime,
                fiscal_direccion: selectedPatient.fiscal_direccion
            });
            setFiscalData({
                rfc: selectedPatient.fiscal_rfc || '',
                razonSocial: selectedPatient.fiscal_razon_social || '',
                usoCfdi: selectedPatient.fiscal_uso_cfdi || 'G03',
                cp: selectedPatient.fiscal_zip || '',
                regime: selectedPatient.fiscal_regime || ''
            });
        }
    }, [selectedPatient]);

    // Fetch full patient data to get fiscal fields
    const fetchPatientData = async (patientId) => {
        try {
            const response = await fetch(`${API_URL}/patients/${patientId}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.ok) {
                const data = await response.json();
                // API might return {data: patient} or just patient
                return data.data || data;
            }
        } catch (error) {
            console.error('Error fetching patient data:', error);
        }
        return null;
    };

    // Handle prefilled patient studies from PendingBilling (can be multiple studies)
    useEffect(() => {
        const loadPrefilledData = async () => {
            if (prefilledPatientStudies && prefilledPatientStudies.studies && prefilledPatientStudies.studies.length > 0) {
                // Set patient search text immediately
                setPatientSearch(`${prefilledPatientStudies.patient_first_name} ${prefilledPatientStudies.patient_last_name}`);

                // Fetch full patient data to get fiscal fields
                const fullPatient = await fetchPatientData(prefilledPatientStudies.patient_id);

                if (fullPatient) {
                    setSelectedPatient(fullPatient);
                } else {
                    // Fallback to basic data if API fails
                    setSelectedPatient({
                        id: prefilledPatientStudies.patient_id,
                        first_name: prefilledPatientStudies.patient_first_name,
                        last_name: prefilledPatientStudies.patient_last_name
                    });
                }

                // Add ALL studies as services
                const newServices = prefilledPatientStudies.studies.map((study, index) => {
                    // Exempt studies should have $0 price
                    const isExempt = !!(study.is_cost_exempt && study.is_cost_exempt !== '0' && study.is_cost_exempt !== 0);
                    return {
                        id: Date.now() + index,
                        patientStudyId: study.id, // Reference to mark as billed later
                        name: study.study_name,
                        catalogId: study.catalog_study_id,
                        quantity: 1,
                        unitPrice: isExempt ? 0 : (parseFloat(study.study_price) || 0),
                        isExempt: isExempt
                    };
                });
                setServices(newServices);
            }
        };

        loadPrefilledData();
    }, [prefilledPatientStudies]);

    const generateFolio = async () => {
        try {
            const response = await fetch(`${API_URL}/receipts/next-folio`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.ok) {
                const data = await response.json();
                setFolio(data.folio);
            } else {
                // Fallback if API fails
                setFolio('R-0000001');
            }
        } catch (error) {
            console.error('Error fetching next folio:', error);
            // Fallback in case of error
            setFolio('R-0000001');
        }
    };

    const fetchCatalogStudies = async () => {
        try {
            const response = await fetch(`${API_URL}/catalog-studies`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.ok) {
                const data = await response.json();
                setCatalogStudies(data);
            }
        } catch (error) {
            console.error('Error loading catalog:', error);
        }
    };

    // Patient search
    const searchPatients = async (query) => {
        if (query.length < 2) {
            setPatients([]);
            setShowPatientList(false);
            return;
        }
        try {
            const url = `${API_URL}/patients?search=${encodeURIComponent(query)}`;
            console.log('Searching patients:', url);

            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.ok) {
                const data = await response.json();
                const patientList = data.data || data;
                console.log('Search results for "' + query + '":', patientList.length, 'patients');
                setPatients(patientList);
                setShowPatientList(true);
            }
        } catch (error) {
            console.error('Error searching patients:', error);
        }
    };

    const selectPatient = (patient) => {
        setSelectedPatient(patient);
        setPatientSearch(`${patient.first_name} ${patient.last_name}${patient.maternal_last_name ? ' ' + patient.maternal_last_name : ''}`);
        setShowPatientList(false);
    };

    // Services management
    const addServiceFromCatalog = (study) => {
        setServices([...services, {
            id: Date.now(),
            name: study.name,
            catalogId: study.id,
            quantity: 1,
            unitPrice: parseFloat(study.price) || 0,
        }]);
        setServiceSearch('');
        setShowServiceList(false);
    };

    const addService = () => {
        setServices([...services, {
            id: Date.now(),
            name: '',
            catalogId: null,
            quantity: 1,
            unitPrice: 0,
        }]);
    };

    const handleExemptConsultation = (checked) => {
        setIsExemptConsultation(checked);
        setServices(prev => prev.map(s => {
            if (s.name.toUpperCase().includes('CONSULTA')) {
                // Store original price if not already stored
                const originalPrice = s.originalPrice !== undefined ? s.originalPrice : s.unitPrice;
                return { 
                    ...s, 
                    isExempt: checked, 
                    unitPrice: checked ? 0 : originalPrice,
                    originalPrice: originalPrice
                };
            }
            return s;
        }));
    };

    const updateService = (id, field, value) => {
        setServices(services.map(s => {
            if (s.id === id) {
                const updated = { ...s, [field]: value };
                // If selecting from catalog, auto-fill price
                if (field === 'catalogId' && value) {
                    const study = catalogStudies.find(cs => cs.id === parseInt(value));
                    if (study) {
                        updated.name = study.name;
                        updated.unitPrice = parseFloat(study.price) || 0;
                    }
                }
                return updated;
            }
            return s;
        }));
    };

    const removeService = (id) => {
        setServices(services.filter(s => s.id !== id));
    };

    // Calculations
    const subtotal = services.reduce((sum, s) => sum + (s.quantity * s.unitPrice), 0);
    const discountAmount = subtotal * (discount / 100);
    const taxableAmount = subtotal - discountAmount;
    const ivaAmount = taxableAmount * 0.16;
    const total = taxableAmount + ivaAmount;

    // Filter services for search
    const filteredServices = catalogStudies.filter(study =>
        study.name.toLowerCase().includes(serviceSearch.toLowerCase()) ||
        (study.code && study.code.toLowerCase().includes(serviceSearch.toLowerCase()))
    );

    // Save receipt
    const handleSave = async () => {
        if (saving || saveLockRef.current) {
            return;
        }

        if (!selectedPatient) {
            setMessage({ type: 'error', text: 'Selecciona un paciente' });
            return;
        }
        if (services.length === 0) {
            setMessage({ type: 'error', text: 'Agrega al menos un servicio' });
            return;
        }

        // Validate doctor only if there's a consultation
        const hasConsultation = services.some(s => s.name.toUpperCase().includes('CONSULTA'));
        if (hasConsultation && !selectedDoctorId) {
            setMessage({ type: 'error', text: 'Debes seleccionar el medico que atendio la consulta' });
            return;
        }

        saveLockRef.current = true;
        setSaving(true);
        try {
            // Build concepto from service names
            const concepto = services.map(s => s.name).join(', ');

            const payload = {
                patient_id: selectedPatient.id,
                date,
                concepto: concepto, // Service names joined
                services: services.map(s => ({
                    name: s.name,
                    catalog_study_id: s.catalogId,
                    patient_study_id: s.patientStudyId || null,
                    quantity: s.quantity,
                    unit_price: s.unitPrice,
                    total: s.quantity * s.unitPrice,
                    isExempt: s.isExempt ? 1 : 0
                })),
                subtotal,
                discount_percent: discount,
                discount_amount: discountAmount,
                iva: ivaAmount,
                isr: 0,
                total,
                payment_method: paymentMethod,
                requires_invoice: requiresInvoice,
                notes: notes,
                // Fiscal data stored separately
                fiscal_cp: requiresInvoice ? fiscalData.cp : null,
                fiscal_regime: requiresInvoice ? fiscalData.regime : null,
                doctor_id: selectedDoctorId !== 'NA' ? selectedDoctorId : null,
                doctor_name: selectedDoctorId && selectedDoctorId !== 'NA' 
                    ? doctors.find(d => d.id.toString() === selectedDoctorId.toString())?.name 
                    : 'SIN MEDICO'
            };

            const response = await fetch(`${API_URL}/receipts`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const savedReceipt = await response.json();
                let markBilledError = null;

                // Mark studies as billed if they have patientStudyId (came from pending billing)
                const studyIds = services
                    .filter(s => s.patientStudyId)
                    .map(s => s.patientStudyId);

                if (studyIds.length > 0) {
                    try {
                        const markBilledResponse = await fetch(`${API_URL}/patient-studies/mark-billed`, {
                            method: 'PATCH',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            },
                            body: JSON.stringify({
                                study_ids: studyIds,
                                receipt_id: savedReceipt.id
                            })
                        });

                        if (!markBilledResponse.ok) {
                            const rawError = await markBilledResponse.text();
                            throw new Error(rawError || `HTTP ${markBilledResponse.status}`);
                        }
                    } catch (err) {
                        console.error('Error marking studies as billed:', err);
                        markBilledError = err;
                    }
                }

                alert('Consulta cobrada'); // Pop up message

                setMessage({
                    type: markBilledError ? 'error' : 'success',
                    text: markBilledError
                        ? 'Recibo guardado, pero no se pudieron marcar todos los estudios como cobrados.'
                        : 'Recibo guardado correctamente'
                });
                // Reset form and clear prefilled
                if (onClear) onClear();
                setTimeout(() => {
                    handleReset();
                }, 1500);
            } else {
                const error = await response.json();
                setMessage({ type: 'error', text: error.error || 'Error al guardar' });
            }
        } catch (error) {
            console.error('Error saving receipt:', error);
            setMessage({ type: 'error', text: 'Error de conexion' });
        } finally {
            setSaving(false);
            saveLockRef.current = false;
            setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const handleReset = () => {
        setSelectedPatient(null);
        setPatientSearch('');
        setServices([]);
        setDiscount(0);
        setRequiresInvoice(false);
        setNotes('');
        setSelectedDoctorId('');
        generateFolio();
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount);
    };

    return (
        <div className="simple-receipt-container">
            <div className="receipt-card">
                {/* Header */}
                <div className="receipt-header">
                    <div className="header-left">
                        <h1><DollarSign size={24} /> Recibo de Pago</h1>
                    </div>
                    <div className="header-right">
                        <div className="folio-box">
                            <span className="folio-label">Folio</span>
                            <span className="folio-value">{folio}</span>
                        </div>
                        <div className="date-box">
                            <span className="date-label">Fecha</span>
                            <input
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                className="date-input"
                            />
                        </div>
                    </div>
                </div>

                {/* Message */}
                {message.text && (
                    <div className={`message-banner ${message.type}`}>
                        {message.text}
                    </div>
                )}

                <div className="receipt-body">
                    {/* LEFT COLUMN: Data Entry */}
                    <div className="receipt-main">
                        {/* Patient Selection */}
                        <div className="section patient-section receipt-block">
                            <h3><User size={18} /> Paciente</h3>
                            <div className="patient-search-wrapper">
                                <input
                                    type="text"
                                    placeholder="Buscar paciente por nombre..."
                                    value={patientSearch}
                                    onChange={(e) => {
                                        setPatientSearch(e.target.value);
                                        searchPatients(e.target.value);
                                    }}
                                    className="patient-search-input"
                                />
                                {selectedPatient && (
                                    <button
                                        className="clear-patient"
                                        onClick={() => {
                                            setSelectedPatient(null);
                                            setPatientSearch('');
                                        }}
                                    >
                                        <X size={16} />
                                    </button>
                                )}
                                {showPatientList && patients.length > 0 && (
                                    <ul className="patient-list">
                                        {patients.slice(0, 5).map(p => (
                                            <li key={p.id} onClick={() => selectPatient(p)}>
                                                <strong>{p.first_name} {p.last_name} {p.maternal_last_name || ''}</strong>
                                                <div style={{ display: 'flex', gap: '10px', fontSize: '0.8rem', color: '#666' }}>
                                                    <span>Exp: {p.patient_code || `ID: ${p.id}`}</span>
                                                    <span>{p.phone || p.email || ''}</span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            {selectedPatient && (
                                <div className="selected-patient-info">
                                    <span><strong>Expediente:</strong> {selectedPatient.patient_code || selectedPatient.id}</span>
                                    {selectedPatient.phone && <span>{selectedPatient.phone}</span>}
                                    {selectedPatient.email && <span>{selectedPatient.email}</span>}
                                </div>
                            )}
                        </div>

                        {/* Services */}
                        <div className="section services-section receipt-block">
                            <h3><FileText size={18} /> Servicios / Consultas</h3>
                            <div className="services-toolbar" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                                <div className="service-search-wrapper" style={{ width: '100%' }}>
                                    <input
                                        type="text"
                                        placeholder="Buscar servicio..."
                                        value={serviceSearch}
                                        onChange={(e) => {
                                            setServiceSearch(e.target.value);
                                            setShowServiceList(true);
                                        }}
                                        onFocus={() => setShowServiceList(true)}
                                        className="service-search-input"
                                    />
                                    {showServiceList && serviceSearch && (
                                        <ul className="service-list">
                                            {filteredServices.length > 0 ? (
                                                filteredServices.slice(0, 8).map(study => (
                                                    <li key={study.id} onClick={() => addServiceFromCatalog(study)}>
                                                        <span>{study.name}</span>
                                                        <span>{formatCurrency(study.price)}</span>
                                                    </li>
                                                ))
                                            ) : (
                                                <li style={{ padding: '0.5rem', color: '#666', fontStyle: 'italic' }}>
                                                    No se encontraron resultados
                                                </li>
                                            )}
                                        </ul>
                                    )}
                                </div>
                            </div>

                            {services.length === 0 ? (
                                <p className="empty-services">No hay servicios agregados</p>
                            ) : (
                                <table className="services-table">
                                    <thead>
                                        <tr>
                                            <th>Servicio</th>
                                            <th>Cant.</th>
                                            <th>P. Unit.</th>
                                            <th>Total</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {services.map(service => (
                                            <tr key={service.id}>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                        <input
                                                            type="text"
                                                            value={service.name}
                                                            onChange={(e) => updateService(service.id, 'name', e.target.value)}
                                                            className="service-select"
                                                            style={{ flex: 1 }}
                                                        />
                                                        {!!service.isExempt && (
                                                            <span className="study-exempt-badge" style={{ fontSize: '0.75rem', padding: '0.15rem 0.4rem', borderRadius: '4px', backgroundColor: '#e8f5e9', color: '#2e7d32', display: 'flex', alignItems: 'center', gap: '0.25rem', whiteSpace: 'nowrap', fontWeight: '500' }}>
                                                                <Gift size={12} /> Exento
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={service.quantity}
                                                        onChange={(e) => updateService(service.id, 'quantity', parseInt(e.target.value) || 1)}
                                                        className="qty-input"
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={service.unitPrice}
                                                        onChange={(e) => updateService(service.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                                                        className="price-input"
                                                        disabled={service.isExempt}
                                                        title={service.isExempt ? "Este estudio está exento de cobro" : ""}
                                                    />
                                                </td>
                                                <td className="total-cell">
                                                    {formatCurrency(service.quantity * service.unitPrice)}
                                                </td>
                                                <td>
                                                    <button className="btn-remove" onClick={() => removeService(service.id)}>
                                                        <Trash2 size={16} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Attending Doctor (Conditional for Consultations) */}
                        {services.some(s => s.name.toUpperCase().includes('CONSULTA')) && (
                            <div className="section doctor-section receipt-block attending-doctor-section" style={{ border: '2px border-left', borderLeftColor: '#083677', paddingLeft: '1.5rem', backgroundColor: '#f8fafc' }}>
                                <h3 style={{ color: '#083677', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <User size={18} /> Medico que Atendió al Paciente
                                </h3>
                                <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.75rem' }}>
                                    Es necesario indicar el medico para el reporte de consultas generales.
                                </p>
                                <select
                                    className="doctor-select-input"
                                    value={selectedDoctorId}
                                    onChange={(e) => setSelectedDoctorId(e.target.value)}
                                    style={{ borderColor: !selectedDoctorId ? '#ef4444' : '#e2e8f0' }}
                                    required
                                >
                                    <option value="" disabled>Seleccionar medico tratante...</option>
                                    {doctors.map(doc => (
                                        <option key={doc.id} value={doc.id}>
                                            {doc.name || `${doc.first_name} ${doc.last_name}`}
                                        </option>
                                    ))}
                                </select>
                                {!selectedDoctorId && (
                                    <span style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: '0.4rem', display: 'block' }}>
                                        * Este campo es obligatorio para consultas.
                                    </span>
                                )}

                                <div className="exempt-consultation-toggle" style={{ 
                                    marginTop: '1.25rem', 
                                    padding: '1rem', 
                                    backgroundColor: '#fff', 
                                    borderRadius: '8px', 
                                    border: '1px solid #e2e8f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }} onClick={() => handleExemptConsultation(!isExemptConsultation)}>
                                    <div style={{ 
                                        width: '20px', 
                                        height: '20px', 
                                        borderRadius: '4px', 
                                        border: '2px solid #083677',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: isExemptConsultation ? '#083677' : 'transparent',
                                        color: '#fff'
                                    }}>
                                        {isExemptConsultation && <Check size={14} strokeWidth={4} />}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <Gift size={16} color={isExemptConsultation ? '#083677' : '#64748b'} />
                                        <span style={{ 
                                            fontWeight: '600', 
                                            color: isExemptConsultation ? '#083677' : '#1e293b',
                                            fontSize: '0.9rem'
                                        }}>
                                            Esta consulta es EXENTA de pago
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* RIGHT COLUMN: Sidebar (Payment & Totals) */}
                    <div className="receipt-sidebar">
                        {/* Payment */}
                        <div className="section payment-section receipt-block">
                            <h3><DollarSign size={18} /> Pago</h3>
                            <div className="payment-form">
                                <div className="form-group">
                                    <label>Metodo</label>
                                    <select
                                        value={paymentMethod}
                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                        className="form-select"
                                    >
                                        {PAYMENT_METHODS.map(pm => (
                                            <option key={pm.value} value={pm.value}>{pm.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <label className={`invoice-checkbox ${requiresInvoice ? 'checked' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={requiresInvoice}
                                        onChange={(e) => setRequiresInvoice(e.target.checked)}
                                        style={{ display: 'none' }}
                                    />
                                    <div className="custom-check">
                                        {requiresInvoice && <Check size={14} strokeWidth={3} />}
                                    </div>
                                    <span>Requiere Factura</span>
                                </label>

                                {requiresInvoice && (
                                    <>
                                        <div className="fiscal-data-form">
                                            <div className="form-group-sm">
                                                <label>Razon Social / Nombre</label>
                                                <input
                                                    type="text"
                                                    value={fiscalData.razonSocial}
                                                    onChange={(e) => setFiscalData({ ...fiscalData, razonSocial: e.target.value })}
                                                    className="form-input-sm"
                                                    placeholder="Nombre Fiscal"
                                                />
                                            </div>
                                            <div className="form-row-sm">
                                                <div className="form-group-sm">
                                                    <label>RFC</label>
                                                    <input
                                                        type="text"
                                                        value={fiscalData.rfc}
                                                        onChange={(e) => setFiscalData({ ...fiscalData, rfc: e.target.value.toUpperCase() })}
                                                        className="form-input-sm"
                                                        placeholder="XAXX010101000"
                                                    />
                                                </div>
                                                <div className="form-group-sm">
                                                    <label>CP</label>
                                                    <input
                                                        type="text"
                                                        value={fiscalData.cp}
                                                        onChange={(e) => setFiscalData({ ...fiscalData, cp: e.target.value })}
                                                        className="form-input-sm"
                                                        placeholder="00000"
                                                    />
                                                </div>
                                            </div>
                                            <div className="form-group-sm">
                                                <label>Uso CFDI</label>
                                                <select
                                                    value={fiscalData.usoCfdi}
                                                    onChange={(e) => setFiscalData({ ...fiscalData, usoCfdi: e.target.value })}
                                                    className="form-select-sm"
                                                >
                                                    <option value="">Seleccionar...</option>
                                                    <option value="G01">G01 - Adquisicion de mercancias</option>
                                                    <option value="G02">G02 - Devoluciones, descuentos o bonificaciones</option>
                                                    <option value="G03">G03 - Gastos en general</option>
                                                    <option value="I01">I01 - Construcciones</option>
                                                    <option value="I02">I02 - Mobiliario y equipo de oficina</option>
                                                    <option value="I03">I03 - Equipo de transporte</option>
                                                    <option value="I04">I04 - Equipo de computo</option>
                                                    <option value="I05">I05 - Dados, troqueles, moldes</option>
                                                    <option value="I06">I06 - Comunicaciones telefonicas</option>
                                                    <option value="I07">I07 - Comunicaciones satelitales</option>
                                                    <option value="I08">I08 - Otra maquinaria y equipo</option>
                                                    <option value="D01">D01 - Honorarios medicos y gastos hospitalarios</option>
                                                    <option value="D02">D02 - Gastos medicos por incapacidad</option>
                                                    <option value="D03">D03 - Gastos funerales</option>
                                                    <option value="D04">D04 - Donativos</option>
                                                    <option value="D05">D05 - Intereses por créditos hipotecarios</option>
                                                    <option value="D06">D06 - Aportaciones voluntarias al SAR</option>
                                                    <option value="D07">D07 - Primas por seguros de gastos medicos</option>
                                                    <option value="D08">D08 - Gastos de transportacion escolar</option>
                                                    <option value="D09">D09 - Depósitos en cuentas para el ahorro</option>
                                                    <option value="D10">D10 - Pagos por servicios educativos</option>
                                                    <option value="S01">S01 - Sin efectos fiscales</option>
                                                    <option value="CP01">CP01 - Pagos</option>
                                                    <option value="CN01">CN01 - Nomina</option>
                                                </select>
                                            </div>
                                            <div className="form-group-sm">
                                                <label>Regimen Fiscal</label>
                                                <select
                                                    value={fiscalData.regime}
                                                    onChange={(e) => setFiscalData({ ...fiscalData, regime: e.target.value })}
                                                    className="form-select-sm"
                                                >
                                                    <option value="">Seleccionar...</option>
                                                    <option value="601">601 - General de Ley PM</option>
                                                    <option value="603">603 - PM con Fines no Lucrativos</option>
                                                    <option value="605">605 - Sueldos y Salarios</option>
                                                    <option value="606">606 - Arrendamiento</option>
                                                    <option value="607">607 - Enajenacion de bienes</option>
                                                    <option value="608">608 - Demas ingresos</option>
                                                    <option value="610">610 - Residentes en el Extranjero</option>
                                                    <option value="611">611 - Ingresos por Dividendos</option>
                                                    <option value="612">612 - PF Actividades Empresariales</option>
                                                    <option value="614">614 - Ingresos por intereses</option>
                                                    <option value="615">615 - Regimen de ingresos por premios</option>
                                                    <option value="616">616 - Sin obligaciones fiscales</option>
                                                    <option value="620">620 - Sociedades Cooperativas</option>
                                                    <option value="621">621 - Incorporacion Fiscal</option>
                                                    <option value="622">622 - Actividades Agricolas</option>
                                                    <option value="623">623 - Opcional para Grupos</option>
                                                    <option value="624">624 - Coordinados</option>
                                                    <option value="625">625 - Hidrocarburos</option>
                                                    <option value="626">626 - RESICO</option>
                                                </select>
                                            </div>
                                        </div>
                                    </>
                                )}

                                <div className="form-group no-print">
                                    <label>Notas</label>
                                    <textarea
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder="Observaciones..."
                                        className="notes-input"
                                        rows={3}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Totals */}
                        <div className="section totals-section receipt-block">
                            <div className="totals-row">
                                <span>Subtotal</span>
                                <span>{formatCurrency(subtotal)}</span>
                            </div>
                            <div className="totals-row discount-row">
                                <div className="discount-label-group">
                                    <span>Descuento</span>
                                    <div className="discount-input-wrapper">
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={discount}
                                            onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                                            className="discount-input"
                                        />
                                        <span>%</span>
                                    </div>
                                </div>
                                <span className="discount-amount">-{formatCurrency(discountAmount)}</span>
                            </div>
                            <div className="totals-row">
                                <span>IVA (16%)</span>
                                <span>{formatCurrency(ivaAmount)}</span>
                            </div>
                            <div className="totals-row total-row">
                                <span>TOTAL</span>
                                <span className="total-value">{formatCurrency(total)}</span>
                            </div>

                            {/* Actions moved inside sidebar for better proximity to total */}
                            <div className="sidebar-actions">
                                <button className="btn-primary full-width" onClick={handleSave} disabled={saving}>
                                    <Save size={18} /> {saving ? 'Guardando...' : 'Cobrar y Guardar'}
                                </button>
                                <div className="secondary-actions">
                                    <button className="btn-secondary btn-danger-hover" onClick={handleReset} style={{ width: '100%' }}>
                                        <X size={16} /> Cancelar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SimpleReceipt;

